import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import Page from '../../../components/page';
import { useInstalledApps } from './hooks/use-installed-apps';
import { messages } from './messages';
import style from './app-store-installed-page.module.less';

import type { AppStoreInstalledApp } from '../api/app-store-api';

export const AppStoreInstalledPage = () => {
  const loadState = useInstalledApps();

  const handleLaunch = useCallback((externalAppUrl: string) => {
    window.open(externalAppUrl, '_blank', 'noopener,noreferrer');
  }, []);

  if (loadState.status === 'loading') {
    return (
      <Page>
        <div className={style.pageContainer}>
          <div className={style.contentContainer}>
            <LoadingIndicator />
          </div>
        </div>
      </Page>
    );
  }

  if (loadState.status === 'error') {
    return (
      <Page>
        <div className={style.pageContainer}>
          <div className={style.contentContainer}>
            <Text className={style.errorText} textType={TextType.Description} value={<FormattedMessage {...messages.failedToLoad} />} />
          </div>
        </div>
      </Page>
    );
  }

  const { apps } = loadState;

  return (
    <Page>
      <div className={style.pageContainer}>
        <div className={style.contentContainer}>
          <FlexColumn gap={Gap.Space24}>
            <Text textType={TextType.H1} value={<FormattedMessage {...messages.pageHeading} />} />

            {apps.length === 0 ? (
              <Text className={style.dimText} textType={TextType.Description} value={<FormattedMessage {...messages.noAppsInstalled} />} />
            ) : (
              <div className={style.appGrid}>
                {apps.map((app) => (
                  <AppCard key={app.appStoreAppVersionGuid} app={app} onLaunch={handleLaunch} />
                ))}
              </div>
            )}
          </FlexColumn>
        </div>
      </div>
    </Page>
  );
};

type AppCardProps = {
  app: AppStoreInstalledApp;
  onLaunch: (externalAppUrl: string) => void;
};

const AppCard = ({ app, onLaunch }: AppCardProps) => {
  const hasUrl = !!app.externalAppUrl;
  const cardClassName = hasUrl ? style.appCard : `${style.appCard} ${style.appCardDisabled}`;

  const handleClick = useCallback(() => {
    if (hasUrl) {
      onLaunch(app.externalAppUrl!);
    }
  }, [hasUrl, app.externalAppUrl, onLaunch]);

  return (
    <div className={cardClassName} onClick={handleClick} role={hasUrl ? 'link' : undefined}>
      <FlexColumn className={style.centerItems} gap={Gap.Space8}>
        {app.iconUrl ? (
          <img alt={app.name} className={style.appIcon} src={app.iconUrl} />
        ) : (
          <div className={style.appIconPlaceholder}>{app.name.charAt(0)}</div>
        )}
        <Text isBold textType={TextType.Label} value={app.name} />
      </FlexColumn>
    </div>
  );
};
